import { Fragment, useMemo } from "react";
import Drawer from "@mui/material/Drawer";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import { createUseStyles } from "$utils/createUseStyles";
import { Collapse } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Link, useNavigate, useLocation } from "react-router-dom";

import clsx from "clsx";
import { useSelector } from "react-redux";
import { theme } from "$utils/theme/bataillog";
import useLocalStorage from "../../hooks/useLocalStorage";
import { publicIconPath } from "$utils/constants";
import InputFilter from "./InputFilter";
import useAuth from "../../hooks/useAuth";
import { storage } from "../../hooks/usePersistedState";
import { appState } from "$reducers";
import useJStyles from "$hooks/useJStyles";

const DRAWER_WIDTH = 227;

const isActivedLink = (el: any, pathname: string) => {
  let path: any = pathname.replace("/", "");
  path = path.split("/");
  if (pathname.startsWith(el.link)) return true;
  return !!(el.link === `/${path[0]}` || el.sublinks?.includes(`/${path[0]}`));
};

function getSideBarItems() {
  return [
    {
      title: "PROJETS",
      icon: "projet",
      link: "/projets",
      sublinks: ["/"],
      children: [],
      displayToClient: true,
      displayToContributor: true,
    },
    {
      title: "CHANTIERS",
      icon: "chantier",
      link: "/chantiers",
      children: [],
      displayToClient: true,
      displayToContributor: true,
    },
    {
      title: "EXPRESSIONS DE BESOIN",
      icon: "expression-besoin",
      link: "/edbs",
      children: [],
      displayToClient: true,
      displayToContributor: true,
    },
    {
      title: "PRÉPARATIONS",
      icon: "preparation",
      link: "/preparations",
      children: [],
      displayToClient: false,
      displayToContributor: false,
    },
    {
      title: "EXPÉDITIONS",
      icon: "shipment",
      link: "/expeditions",
      children: [],
      displayToClient: false,
      displayToContributor: false,
    },
    {
      title: "MATÉRIEL",
      icon: "materiel",
      displayToClient: true,
      displayToContributor: true,
      children: [
        {
          title: "Catalogue",
          link: "/catalogue",
          sublinks: ["/categories"],
          displayToClient: true,
          displayToContributor: true,
        },
        {
          title: "Stock",
          link: "/stock",
          sublinks: ["/stock"],
          displayToClient: true,
          displayToContributor: true,
        },
        {
          title: "Entrepôts",
          link: "/entrepot",
          displayToClient: false,
          displayToContributor: false,
        },
        {
          title: "Commandes",
          link: "/commandes",
          displayToClient: true,
          displayToContributor: true,
        },
        {
          title: "Réceptions",
          link: "/receptions",
          displayToClient: true,
          displayToContributor: true,
        },
      ],
    },
    {
      title: "UTILISATEURS",
      icon: "utilisateur",
      displayToClient: false,
      displayToContributor: false,
      children: [
        {
          title: "Entreprises",
          link: "/entreprise",
          displayToClient: true,
          displayToContributor: true,
        },
        {
          title: "Coordinateurs logistique",
          link: "/coordinators",
          displayToClient: true,
          displayToContributor: true,
        },
      ],
    },
  ];
}

function Sidebar() {
  const classes = useStyles();
  const { jclasses } = useJStyles();
  const navigate = useNavigate();
  const [open, setOpen] = useLocalStorage("openedSidebar", [false]);
  const { pathname } = useLocation();
  const { isAdmin, isClient, isContributor } = useAuth();
  const { filter: companySidebarFilter } = useSelector(appState);
  const sidebarItems = useMemo(() => {
    return getSideBarItems()
      .filter(
        (sidebarItem) =>
          isAdmin() ||
          (isClient() && sidebarItem.displayToClient) ||
          (isContributor() && sidebarItem.displayToContributor)
      )
      .map((sidebarItem) => ({
        ...sidebarItem,
        children: (sidebarItem.children as any).filter(
          (child: any) =>
            isAdmin() ||
            (isClient() && child.displayToClient) ||
            (isContributor() && child.displayToContributor)
        ),
      }));
  }, [isAdmin, isClient, isContributor]);

  const handleClick = (index: number, link?: string) => {
    const openNed = [...open, ...Array(index).fill(false)]
      .slice(0, Math.max(open.length, index + 1))
      .map((p: boolean, i: number) => (i === index ? !p : p));
    setOpen(openNed);
    if (link) {
      storage.clear();
      navigate(link);
    }
  };

  // TODO a analyser pour reactiver
  // open the sidebar corresponding to actived link or actived child link
  /* useLayoutEffect(() => {
    setOpen((oldOpen) => {
      const openNed = [
        ...oldOpen,
        ...Array(sidebareFiltered.length).fill(false),
      ];
      sidebareFiltered.forEach((s: any, index: number) => {
        s.children.forEach((c: any) => {
          if (isActivedLink(c, pathname)) {
            openNed[index] = true;
          }
        });
      });
      return openNed;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOpen, sidebareFiltered]);
  // on enleve pathname des dependances pour ne l'executer qu'une seule fois au démarrage sinon ca rame
  */

  return (
    <Drawer
      className={`${classes.drawer} ${jclasses.jWrapper}`}
      variant="permanent"
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      <Toolbar />
      {isAdmin() && <InputFilter />}
      <div className={classes.drawerContainer}>
        <List className={classes.list}>
          {sidebarItems.map((s: any, index: number) => (
            <Fragment key={s.title}>
              <ListItem
                button
                key={s.title}
                onClick={() => handleClick(index, s.link)}
                className={clsx(
                  classes.nested,
                  isActivedLink(s, pathname) && classes.activeLink
                )}
              >
                <ListItemIcon className={classes.itemIcons}>
                  <img
                    alt="ico-batail"
                    src={`${publicIconPath}/${s.icon}.svg`}
                  />
                </ListItemIcon>
                <div className={classes.itemTitle}>{s.title}</div>
                {s.children.length > 0 && open[index] && <ExpandLess />}
                {s.children.length > 0 && !open[index] && <ExpandMore />}
              </ListItem>

              {s.children.length > 0 && (
                <Collapse in={open[index]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {s.children.map((child: any) => {
                      if (child.link) {
                        let { link } = child;
                        if (
                          child.link === "/entreprise" &&
                          companySidebarFilter?._id
                        ) {
                          link += `/${companySidebarFilter?._id}`;
                        }
                        return (
                          <Link
                            key={child.title}
                            to={link}
                            className={classes.linkText}
                            onClick={() => {
                              storage.clear();
                            }}
                          >
                            <ListItem
                              button
                              className={clsx(
                                classes.nestedSub,
                                isActivedLink(child, pathname) &&
                                  classes.activeLink
                              )}
                              key={child.title}
                            >
                              {child.title}
                            </ListItem>
                          </Link>
                        );
                      }
                      return (
                        <ListItem
                          button
                          className={clsx(
                            classes.nestedSub,
                            isActivedLink(child, pathname) && classes.activeLink
                          )}
                          key={child.title}
                        >
                          {child.title}
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </Fragment>
          ))}
        </List>
      </div>
    </Drawer>
  );
}

const useStyles = createUseStyles(() => ({
  activeLink: {
    "&.MuiButtonBase-root": {
      backgroundColor: theme.color.blueLighter,
      borderLeft: `3px solid ${theme.color.blue}`,
    },
  },
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 600,
  },
  drawerPaper: {
    "&.MuiPaper-root": {
      backgroundColor: theme.color.blueLight,
      "& .MuiList-root": {
        maxHeight: "unset !important",
      },
    },
    width: DRAWER_WIDTH,
  },
  drawerContainer: {
    overflow: "auto",
  },
  list: {
    color: theme.color.white,
  },
  nested: {
    "&.MuiListItem-gutters": {
      paddingLeft: theme.spacing(4),
      margin: [10, 0],
    },
  },
  nestedSub: {
    "&.MuiListItem-gutters": {
      paddingLeft: theme.spacing(10),
      fontWeight: 400,
      margin: [10, 0],
      "&:first-of-type": {
        marginTop: 0,
      },
    },
  },
  itemIcons: {
    "&.MuiListItemIcon-root": {
      minWidth: theme.spacing(6),
    },
  },
  itemTitle: {
    flex: 1,
  },
  linkText: {
    textDecoration: "unset",
    color: theme.color.white,
  },
}));

export default Sidebar;
